/**
 * Module Contrats — dialog de création / édition d'un contrat.
 *
 * Champs : nom, type, fournisseur, prix d'abonnement, fréquence de paiement,
 * lien espace client, référence client, dates, notes.
 */
import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material'

import { contratsApi } from '../api/contratsApi'
import {
  CONTRACTS_ACCENT,
  CONTRACT_TYPES,
  darkFieldSx,
  PAGE_BG,
  PAYMENT_FREQUENCIES,
} from './shared'

const EMPTY = {
  name: '',
  contract_type: 'electricity',
  provider: '',
  subscription_price: '',
  payment_frequency: 'monthly',
  customer_portal_url: '',
  customer_reference: '',
  start_date: '',
  end_date: '',
  notes: '',
  is_active: true,
}

export default function ContractDialog({ open, contract, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setError(null)
      setForm(
        contract
          ? {
              ...EMPTY,
              ...contract,
              subscription_price: contract.subscription_price ?? '',
              start_date: contract.start_date || '',
              end_date: contract.end_date || '',
            }
          : EMPTY
      )
    }
  }, [open, contract])

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }))

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Le nom du contrat est obligatoire.')
      return
    }
    setSaving(true)
    const body = {
      name: form.name.trim(),
      contract_type: form.contract_type,
      provider: form.provider,
      subscription_price: form.subscription_price === '' ? '0' : form.subscription_price,
      payment_frequency: form.payment_frequency,
      customer_portal_url: form.customer_portal_url,
      customer_reference: form.customer_reference,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      notes: form.notes,
      is_active: form.is_active,
    }
    const saved = contract
      ? await contratsApi.contracts.update(contract.id, body)
      : await contratsApi.contracts.create(body)
    setSaving(false)
    if (saved) onSaved()
    else setError("Échec de l'enregistrement. Vérifiez les champs (URL valide ?).")
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: PAGE_BG, border: '1px solid rgba(255,255,255,0.1)' } }}
    >
      <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>
        {contract ? 'Modifier le contrat' : 'Nouveau contrat'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Nom du contrat"
              placeholder="Électricité maison"
              value={form.name}
              onChange={set('name')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label="Type"
              value={form.contract_type}
              onChange={set('contract_type')}
              sx={darkFieldSx}
            >
              {CONTRACT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fournisseur"
              placeholder="EDF, Engie, Free…"
              value={form.provider}
              onChange={set('provider')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Abonnement (€)"
              value={form.subscription_price}
              onChange={set('subscription_price')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              label="Fréquence"
              value={form.payment_frequency}
              onChange={set('payment_frequency')}
              sx={darkFieldSx}
            >
              {PAYMENT_FREQUENCIES.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Lien espace client"
              placeholder="https://…"
              value={form.customer_portal_url}
              onChange={set('customer_portal_url')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Référence client"
              value={form.customer_reference}
              onChange={set('customer_reference')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="date"
              label="Début"
              InputLabelProps={{ shrink: true }}
              value={form.start_date}
              onChange={set('start_date')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="date"
              label="Fin"
              InputLabelProps={{ shrink: true }}
              value={form.end_date}
              onChange={set('end_date')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={set('notes')}
              sx={darkFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={set('is_active')}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: CONTRACTS_ACCENT },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: CONTRACTS_ACCENT,
                    },
                  }}
                />
              }
              label="Contrat actif"
              sx={{ color: '#fff' }}
            />
          </Grid>
        </Grid>
        {error && (
          <div style={{ color: '#ff7a7a', marginTop: 8, fontSize: 14 }}>{error}</div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          sx={{
            bgcolor: CONTRACTS_ACCENT,
            color: '#00001d',
            fontWeight: 700,
            '&:hover': { bgcolor: '#ffc76e' },
          }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
